import { formatToDollar } from "@inrev/common";
import type { AdminSuretyBond } from "@portal/api/bond/admin";
import { Card, Text } from "@portal/components/primitives";
import { useMemo } from "react";
import { suretyBondClosedReasonTypeLabelMap } from "../../../../constants";
import { formatEmpty, formatFromISODate } from "../../../../utils";
import { AdminDataList, AdminDataListProps } from "../../../shared/admin/AdminDataList";

type AdminBondClosingDetailsCardProps = {
	bond: AdminSuretyBond;
};

export const AdminBondClosingDetailsCard = ({ bond }: AdminBondClosingDetailsCardProps) => {
	const dataList = useMemo(() => {
		if (bond.status !== "closed") return [];

		const data: AdminDataListProps["data"] = [
			{ label: "Closed Date", value: formatEmpty(formatFromISODate(bond.closedAt)) },
			{
				label: "Closed Reason",
				value: formatEmpty(
					!!bond.closedReason ? suretyBondClosedReasonTypeLabelMap[bond.closedReason] : undefined,
				),
			},
		];

		if (bond.contractSuretyType === "final" && bond.closedReason === "project_completed") {
			data.push({
				label: "Actual Completion Date",
				value: formatEmpty(formatFromISODate(bond.actualCompletionDate)),
			});
			data.push({
				label: "Final Contract Price",
				value: formatEmpty(formatToDollar(bond.actualContractAmount)),
			});
		}

		data.push({ label: "Closed Note", value: formatEmpty(bond.closedNote) });

		return data;
	}, [bond]);

	if (bond.status !== "closed") return null;

	return (
		<Card elevation="raised" className="rounded-xl" data-testid="admin-bond-closing-details-card">
			<Card.Body className="p-6">
				<div className="flex flex-col w-full space-y-5">
					<Text type="title" color="primary">
						Closing Details
					</Text>
					<AdminDataList data={dataList} />
				</div>
			</Card.Body>
		</Card>
	);
};
